import React, { useState } from 'react';
import { Plus, Search } from 'lucide-react';
import { useCustomers } from '../lib/hooks';
import type { Database } from '../lib/database.types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CustomerCard } from '../components/customers/CustomerCard';
import { CustomerProfile } from '../components/customers/CustomerProfile';
import AddCustomer from './customers/AddCustomer';

type Customer = Database['public']['Tables']['customers']['Row'];

const Customers = () => {
  const { customers, loading, error } = useCustomers();
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredCustomers = customers.filter(customer => 
    `${customer.first_name} ${customer.last_name} ${customer.email}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  // Listen for viewCustomer events
  React.useEffect(() => {
    const handleViewCustomer = (e: CustomEvent<Customer>) => {
      setSelectedCustomer(e.detail);
    };
    window.addEventListener('viewCustomer', handleViewCustomer as EventListener);
    return () => {
      window.removeEventListener('viewCustomer', handleViewCustomer as EventListener);
    };
  }, []);

  if (showAddForm) {
    return <AddCustomer />;
  }

  if (selectedCustomer) {
    return (
      <CustomerProfile
        customer={selectedCustomer}
        onBack={() => setSelectedCustomer(null)}
      />
    );
  }

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customers</h1>
          <p className="text-muted-foreground">Manage your customer relationships</p>
        </div>
        <Button onClick={() => setShowAddForm(true)}>
          <Plus className="w-5 h-5 mr-2" />
          Add Customer
        </Button>
      </div>

      <div className="mb-6">
        <div className="relative max-w-md">
          <Input
            type="text"
            placeholder="Search customers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
          <Search className="absolute left-3 top-2.5 w-5 h-5 text-muted-foreground" />
        </div>
      </div>

      {loading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="h-6 bg-muted rounded w-1/4 mb-4"></div>
                <div className="h-4 bg-muted rounded w-1/3"></div>
              </CardHeader>
            </Card>
          ))}
        </div>
      ) : error ? (
        <Card className="bg-destructive/10 text-destructive">
          <CardHeader>
            <CardTitle>Error</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredCustomers.map((customer) => (
            <CustomerCard
              key={customer.id}
              customer={customer}
              onClick={() => setSelectedCustomer(customer)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Customers;